import clsx from 'clsx';
import { PrimaryButton } from 'Components/Buttons';
import CameraSelector from 'Components/CameraSelector';
import RoundButton from 'Components/RoundButton';
import { Box, Grommet, RangeInput as InputRange, Text } from 'grommet';
import {
  ChevronLeftIcon,
  ClosedCaptionIcon,
  EllipsisVerticalIcon,
  MaximizeIcon,
  MinimizeIcon,
  PauseIcon,
  PlayIcon,
  Volume2Icon,
  VolumeXIcon,
} from 'lucide-preact';
import { forwardRef } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import { connect, useDispatch } from 'react-redux';
import { logGAEvent } from 'Shared/ga';

import { useSelector } from '@/redux/helper';
import { setMuted, setVolume } from '@/redux/slices/settings/videoPlayerConfig/action';

import { formatSecondsToVideoDuration } from '../../helpers';
import { useIsHabitatTabbed, useIsMobileSize } from '../../hooks';
import { setShowVivTranscript } from '../../redux/actions';
import style from './style.scss';

const customThemeRangeInputVolume = {
  global: {
    spacing: '12px',
  },
  rangeInput: {
    track: {
      height: '6px',
      extend: () => `border-radius: 10px`,
      lower: {
        color: 'white',
        opacity: 0.7,
      },
      upper: {
        color: 'white',
        opacity: 0.3,
      },
    },
    thumb: {
      color: 'white',
    },
  },
};

const customThemeRangeInputSeekbar = {
  global: {
    spacing: '12px',
  },
  rangeInput: {
    track: {
      height: '6px',
      extend: () => `border-radius: 10px`,
      lower: {
        color: '#2E2D2D',
        opacity: 0.7,
      },
      upper: {
        color: 'white',
        opacity: 0.3,
      },
    },
    thumb: {
      color: '#2E2D2D',
    },
  },
};

const VideoControls = (
  {
    showControls,
    showPlayControl,
    showVolumeControl,
    showFullscreenControl,
    showSeekBar,
    showTimeStats,
    showPIPControl,
    singleButton = false,
    onPauseHandler,
    isPlaying,
    isLoading,
    videoLength,
    timeElapsed,
    updateVideoTimeHandler,
    showSwitchCameraControl,
    onChangeFullscreen,
    mode = 'webrtc',
    muted = true,
    showVivTranscriptControl = false,
    vivSupportsTranscript,
    showVivTranscript = false,
    setShowVivTranscriptAction,
    onNextHandler,
    onMuteChange,
  },
  ref,
) => {
  const dispatch = useDispatch();
  const volume = useSelector((state) => state.settings.videoPlayerConfig.volume);
  const [showVolumeBar, setShowVolumeBar] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isMobileSize = useIsMobileSize();
  const [expandControls, setExpandControls] = useState(!isMobileSize);
  const volumeButtonRef = useRef(null);
  const isTabbedView = useIsHabitatTabbed();

  useEffect(() => {
    setIsMuted(muted);
  }, [muted]);

  useEffect(() => {
    const handleFullscreenMode = () => {
      const isFullscreen = document.fullscreenElement || document.webkitCurrentFullScreenElement;
      setIsFullscreen(isFullscreen);
      onChangeFullscreen(isFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenMode);
    document.addEventListener('webkitfullscreenchange', handleFullscreenMode);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenMode);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenMode);
    };
  }, [setIsFullscreen, onChangeFullscreen]);

  // TODO: we need to refactor this
  // there are a lot of references passed between different components
  // and this is receiving a reference that's not a video element
  // so the "video.play()" is throwing errors
  // eslint-disable-next-line react/destructuring-assignment
  const video = ref.current;

  const togglePlay = (evt) => {
    evt.stopPropagation();
    if (typeof onPauseHandler === 'function') {
      onPauseHandler();
    }
  };

  const toggleMute = (evt) => {
    evt.stopPropagation();
    const newValue = !video.muted;
    video.muted = newValue;
    setIsMuted(newValue);
    onMuteChange?.(newValue);
    dispatch(setMuted(newValue));
    // set the volume to 0.1 when unmuted video to avoid the volume is still 0
    if (!newValue && volume === 0) {
      dispatch(setVolume(0.1));
    }
  };

  const onVolumeChange = ({ target: { value } }) => {
    video.volume = value;
    dispatch(setVolume(value));
    // set the muted state to true when the volume is 0
    const isGoingToMute = value === '0';
    video.muted = isGoingToMute;
    setIsMuted(isGoingToMute);
    onMuteChange?.(isGoingToMute);
  };

  const openFullscreen = (evt) => {
    evt.stopPropagation();
    if (!isFullscreen) {
      video.requestFullscreen(mode);
    } else {
      video.exitFullscreen();
    }
  };

  const togglePictureInPicture = (evt) => {
    evt.stopPropagation();
    video.requestPictureInPicture();
  };

  const onChangeCCState = () => {
    logGAEvent('change-cc-state', showVivTranscript ? 'False' : 'True');
    setShowVivTranscriptAction(!showVivTranscript);
  };

  if (!showControls || !video) {
    return null;
  }

  return (
    <Box className={clsx(style.videoControls, { [style.mobile]: isTabbedView })} direction="column" align="start">
      {typeof onNextHandler === 'function' && (
        <Box direction="row" fill pad={{ vertical: 'xsmall' }} margin={{ bottom: 'small' }} justify="end">
          <PrimaryButton label="Next Video" onClick={() => onNextHandler()} />
        </Box>
      )}

      <Box direction="row" width="100%">
        {singleButton && isMobileSize && (
          <RoundButton
            onClick={() => setExpandControls(!expandControls)}
            width="28"
            backgroundColor="var(--charcoal)"
            color="white"
            className={style.showControlsButton}
          >
            {expandControls ? <ChevronLeftIcon className="size-[1em]" /> : <EllipsisVerticalIcon className="size-[1em]" />}
          </RoundButton>
        )}

        {/* eslint-disable-next-line max-len */}
        <div className={clsx(style.buttonsWrapper, singleButton && !expandControls && isMobileSize && style.hidden)}>
          {showPlayControl && (
            <RoundButton onClick={togglePlay} width="28" backgroundColor="var(--charcoal)" color="white" loading={isLoading}>
              {isPlaying ? <PauseIcon className="size-[1em]" /> : <PlayIcon className="size-[1em]" />}
            </RoundButton>
          )}

          {showVolumeControl && (
            <div
              ref={volumeButtonRef}
              onMouseEnter={() => setShowVolumeBar(true)}
              onMouseLeave={() => setShowVolumeBar(false)}
              className={clsx(style.volumeWrapper, {
                [style.expanded]: showVolumeBar && !isTabbedView,
              })}
              data-testid="video-volume-control-button"
            >
              <RoundButton onClick={toggleMute} width="28" backgroundColor="var(--charcoal)" color="white">
                {isMuted ? <VolumeXIcon className="size-[1em]" /> : <Volume2Icon className="size-[1em]" />}
              </RoundButton>
              {!isTabbedView && showVolumeBar && volumeButtonRef.current && (
                <div className={clsx(style.volume, { [style.expanded]: showVolumeBar })}>
                  <Grommet theme={customThemeRangeInputVolume} className={style.rangeInput}>
                    <InputRange
                      min={0}
                      max={1}
                      step={0.1}
                      value={volume}
                      onChange={onVolumeChange}
                      data-testid="volume-control-input"
                    />
                  </Grommet>
                </div>
              )}
            </div>
          )}

          {showPIPControl && (
            <RoundButton onClick={togglePictureInPicture} width="28" backgroundColor="var(--charcoal)" color="white">
              <MinimizeIcon className="size-[1em]" />
            </RoundButton>
          )}

          {showFullscreenControl && (
            <RoundButton onClick={openFullscreen} width="28" backgroundColor="var(--charcoal)" color="white">
              <MaximizeIcon className="size-[1em]" />
            </RoundButton>
          )}

          {showSwitchCameraControl && <CameraSelector />}

          {mode === 'vod' && (
            <div className={style.timelineWrapper}>
              {showSeekBar && (
                <div className={style.seekBar}>
                  <Grommet theme={customThemeRangeInputSeekbar} className={style.rangeInput}>
                    <InputRange
                      min={0}
                      max={videoLength}
                      step={1}
                      value={timeElapsed}
                      onChange={({ target: { value } }) => updateVideoTimeHandler(value)}
                    />
                  </Grommet>
                </div>
              )}
              {showTimeStats && (
                <div className={style.timeStats}>
                  <Text color="white" weight={700} size="large" margin={{ left: '5px' }}>
                    {`${formatSecondsToVideoDuration(timeElapsed)} / ${formatSecondsToVideoDuration(videoLength)}`}
                  </Text>
                </div>
              )}
            </div>
          )}

          {showVivTranscriptControl && vivSupportsTranscript && (
            <RoundButton
              onClick={onChangeCCState}
              width="28"
              backgroundColor={showVivTranscript ? 'white' : 'var(--charcoal)'}
              color={showVivTranscript ? 'var(--charcoal)' : 'white'}
            >
              <ClosedCaptionIcon className="size-[1em]" />
            </RoundButton>
          )}
        </div>
      </Box>
    </Box>
  );
};

export default connect(
  ({ mainStream: { showVivTranscript, vivSupportsTranscript } }) => ({
    showVivTranscript,
    vivSupportsTranscript,
  }),
  {
    setShowVivTranscriptAction: setShowVivTranscript,
  },
  null,
  {
    forwardRef: true,
  },
)(forwardRef(VideoControls));
